import React, { FC } from 'react';
import { useSelector } from 'react-redux';

import { isEmpty } from 'lodash/fp';

import { FlexRow, H2, PageHeader } from '@portal/common/components';
import { TYPE_MAP_CHART } from '@portal/common/models/chart-type';
import { DataExplorerSelectionResource } from '@portal/common/types';

import { generateDataExplorerSelection } from '../../components/DataExplorer/utils';
import AddToLists from '../../components/exports/add-to-lists';
import ExportPDF from '../../components/exports/export-pdf';
import SharePermalink from '../../components/exports/share-permalink';
import _ from '../../locale';
import {
    getDataCollections,
    getDataTableSortedByColumn,
    getDataTableSortedDescending,
    getDataTableVisibleColumns,
    getDataToolConfig,
    getSelectedChartType,
    getSelectedConditions,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataType,
    getSelectedRefinementFilters,
    getVisibleRefinementFilters,
} from '../../store/data-explorer/selectors';
import {
    getSelectedDataCollectionForDataTool,
    getSelectedDataTool,
} from '../../store/user-settings/selectors';

interface EDPHeaderProps {
    areChartsHidden: boolean;
}

const EDPHeader: FC<EDPHeaderProps> = ({ areChartsHidden }) => {
    const { title } = useSelector(getDataToolConfig);
    const dataCollections = useSelector(getDataCollections);

    const dataTool = useSelector(getSelectedDataTool);
    const selectedDataCollection = useSelector(getSelectedDataCollectionForDataTool);
    const selectedConditions = useSelector(getSelectedConditions);
    const selectedRefinementFilters = useSelector(getSelectedRefinementFilters);
    const granularity = useSelector(getSelectedConditionsRefinedGranularity);
    const visibleRefinementFilters = useSelector(getVisibleRefinementFilters);
    const selectedDataType = useSelector(getSelectedDataType);
    const sortedByColumn = useSelector(getDataTableSortedByColumn);
    const sortedDescending = useSelector(getDataTableSortedDescending);
    const visibleColumns = useSelector(getDataTableVisibleColumns);
    const selectedChartType = useSelector(getSelectedChartType);

    const areDataCollectionsEmpty = isEmpty(dataCollections);

    const getDataExplorerSelection = (): DataExplorerSelectionResource =>
        generateDataExplorerSelection(
            dataTool,
            selectedDataCollection,
            selectedConditions,
            selectedRefinementFilters,
            granularity,
            visibleRefinementFilters,
            selectedDataType,
            sortedByColumn,
            sortedDescending,
            visibleColumns,
            selectedChartType,
            getChartSettings()
        );

    const getChartSettings = () => {
        const mapChartSettings = {}; //{ detailLevel, type: mapType };

        return {
            ...(selectedChartType === TYPE_MAP_CHART && mapChartSettings),
            areVisible: !areChartsHidden,
        };
    };

    return (
        <PageHeader>
            <FlexRow justify="space-between" grow={1} align="center">
                <H2>{_(title)}</H2>
                {!areDataCollectionsEmpty && (
                    <FlexRow itemsSpacing={15}>
                        <SharePermalink getExplorerSelection={getDataExplorerSelection} />
                        <AddToLists getExplorerSelection={getDataExplorerSelection} />
                        <ExportPDF areChartsHidden={areChartsHidden} />
                    </FlexRow>
                )}
            </FlexRow>
        </PageHeader>
    );
};

export default EDPHeader;
